import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button, Offcanvas, Nav, Modal } from 'react-bootstrap';
import { 
  List, 
  Pen, 
  People, 
  ShieldShaded, 
  Coin, 
  ReceiptCutoff, 
  Tree, 
  Globe2, 
  House, 
  Bell, 
  Folder, 
  Search, 
  Info, 
  Lock, 
  HeartFill,
  ChevronRight,
  GeoAlt,
  Map,
  Palette,  // Added for theme settings
  Gear,
  ShieldCheck
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import departmentConfig from '../../utils/departmentConfig';
import StateButtonPage from '../pages/StateButtonPage';
import ProvinceButtonPage from '../pages/ProvinceButtonPage';
import MexicanStateButtonPage from '../pages/MexicanStateButtonPage';
import KingdomButtonPage from '../pages/KingdomButtonPage';
import WhatsNewModal, { WHATS_NEW_VERSION, markWhatsNewSeen } from '../whatsnew/WhatsNewModal';
import { getCountryConfig, utilityNavItems } from '../../utils/navigationConfig';
import { getCountryInfo, getActiveCountries } from '../../utils/countryConfig';
import { Container, Row, Col } from 'react-bootstrap';
import SpendingIcon from './SpendingIcon';

// The More section lists these by hand, in this order: Preferences,
// Collection (moved here when Spending took its tab in the bottom navbar),
// Fact Checker and Social. utilityNavItems is rendered after them, so any
// entry of its own that points at one of the same pages is skipped -- that is
// what had Fact Checker showing twice. Matched on the path rather than the id,
// because the path is what makes two links the same link.
const LISTED_BY_HAND = new Set(['/preferences', '/collection', '/fact-check', '/social']);

const normalizeNavPath = (path) => {
  const value = String(path || '').split(/[?#]/)[0];
  return value.length > 1 ? value.replace(/\/+$/, '') : value;
};

const isListedByHand = (item) => LISTED_BY_HAND.has(normalizeNavPath(item.path));

const MobileNavigation = ({ currentCountry: currentCountryProp = 'us' }) => {
  const { t } = useTranslation('common');
  const [offcanvasIsOpen, setOffcanvasIsOpen] = useState(false);
  const [statesModalIsOpen, setStatesModalIsOpen] = useState(false);
  const [provincesModalIsOpen, setProvincesModalIsOpen] = useState(false);
  const [mexicanStatesModalIsOpen, setMexicanStatesModalIsOpen] = useState(false);
  const [ukKingdomsModalIsOpen, setUkKingdomsModalIsOpen] = useState(false);
  const [countriesModalIsOpen, setCountriesModalIsOpen] = useState(false);
  const [comingSoonModalIsOpen, setComingSoonModalIsOpen] = useState(false);
  const [whatsNewIsOpen, setWhatsNewIsOpen] = useState(false);
  const [whatsNewPending, setWhatsNewPending] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    departments: false,
    topics: true,
    branches: true
  });
  
  const navigate = useNavigate();
  const location = useLocation();

  const isAllCountriesPage = () => {
    const path = location.pathname;
    return path === '/' || path === '/all';
  };

  const getCountryFromPath = () => {
    const path = location.pathname;
    if (path === '/' || path === '/all') return 'all';
    if (path.startsWith('/canada')) return 'canada';
    if (path.startsWith('/mexico')) return 'mexico';
    if (path.startsWith('/uk')) return 'uk';
    if (path.startsWith('/us')) return 'us';
    if (path.startsWith('/australia')) return 'australia';
    if (path.startsWith('/france')) return 'france';
    if (path.startsWith('/italy')) return 'italy';
    if (path.startsWith('/japan')) return 'japan';
    if (path.startsWith('/south-africa')) return 'south-africa';
    if (path.startsWith('/ukraine')) return 'ukraine';
    const storedCountry = localStorage.getItem('selectedCountry');
    if (storedCountry) return storedCountry;
    return 'us';
  };

  const currentCountry = getCountryFromPath() || currentCountryProp;

  const normalizeCountrySlug = (slug) => {
    const normalizations = {
      'united_states_of_america': 'us',
      'united_states': 'us',
      'usa': 'us',
      'united_kingdom': 'uk',
      'ca': 'canada',
      'mx': 'mexico'
    };
    return normalizations[slug] || slug;
  };

  const getEffectiveCountry = () => {
    if (currentCountry === 'all') return 'all';
    const normalizedProp = normalizeCountrySlug(currentCountry);
    if (normalizedProp && normalizedProp !== 'us') {
      return normalizedProp;
    }
    const storedCountry = localStorage.getItem('selectedCountry');
    if (storedCountry) {
      return normalizeCountrySlug(storedCountry);
    }
    return 'us';
  };

  const effectiveCountry = getEffectiveCountry();

  const isAllView = currentCountry === 'all' || effectiveCountry === 'all';

  const fullyImplementedCountries = ['us', 'ca', 'mx', 'uk', 'united_states_of_america', 'canada', 'mexico', 'united_kingdom'];
  const isFullyImplemented = !isAllView && fullyImplementedCountries.includes(currentCountry);

  const countryConfig = isAllView ? null : getCountryConfig(currentCountry);
  
  const currentCountryInfo = isAllView ? null : (getCountryInfo ? getCountryInfo(currentCountry) : null);
  
  const fallbackCountryInfo = {
    all: { name: 'All Countries', flag: '🌐' },
    us: { name: 'United States', flag: '🇺🇸' },
    united_states_of_america: { name: 'United States', flag: '🇺🇸' },
    ca: { name: 'Canada', flag: '🇨🇦' },
    canada: { name: 'Canada', flag: '🇨🇦' },
    mx: { name: 'Mexico', flag: '🇲🇽' },
    mexico: { name: 'Mexico', flag: '🇲🇽' },
    uk: { name: 'United Kingdom', flag: '🇬🇧' },
    united_kingdom: { name: 'United Kingdom', flag: '🇬🇧' },
    australia: { name: 'Australia', flag: '🇦🇺' },
    france: { name: 'France', flag: '🇫🇷' },
    italy: { name: 'Italy', flag: '🇮🇹' },
    japan: { name: 'Japan', flag: '🇯🇵' },
    'south-africa': { name: 'South Africa', flag: '🇿🇦' },
    ukraine: { name: 'Ukraine', flag: '🇺🇦' }
  };
  
  const finalCountryInfo = isAllView 
    ? { name: 'All Countries', flag: '🌐' }
    : (currentCountryInfo || fallbackCountryInfo[currentCountry] || countryConfig || {
        name: currentCountry.charAt(0).toUpperCase() + currentCountry.slice(1).replace(/_/g, ' '),
        flag: '🌐'
      });
  
  const departments = (isFullyImplemented && countryConfig?.departments) ? 
    countryConfig.departments.map(deptId => {
      const deptConfig = departmentConfig[deptId];
      return deptConfig ? {
        id: deptId,
        title: deptConfig.title,
        path: deptConfig.mainUrl
      } : null;
    }).filter(Boolean) : [];

  const genericBranches = [
    { id: 'legislative', title: 'Legislative', icon: 'pen' },
    { id: 'executive', title: 'Executive', icon: 'people' },
    { id: 'judicial', title: 'Judicial', icon: 'gavel' }
  ];

  const genericTopics = [
    { id: 'defense', title: 'Defense', icon: 'shield' },
    { id: 'economy', title: 'Economy', icon: 'coin' },
    { id: 'elections', title: 'Elections', icon: 'receipt' },
    { id: 'environment', title: 'Environment', icon: 'tree' },
    { id: 'foreign-affairs', title: 'Foreign Affairs', icon: 'globe' },
    { id: 'health', title: 'Health', icon: 'heart-pulse' },
    { id: 'science-tech', title: 'Science & Tech', icon: 'rocket' },
    { id: 'spending', title: 'Spending', icon: 'dollar-sign' },
    { id: 'infrastructure', title: 'Infrastructure', icon: 'building' },
    { id: 'regulations', title: 'Regulations', icon: 'file-text' },
    { id: 'justice', title: 'Justice', icon: 'person-arms-up' }
  ];

  const handleNavClick = (path, external = false) => {
    setOffcanvasIsOpen(false);
    if (external) {
      window.open(path, '_blank');
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const handleComingSoonClick = () => {
    setOffcanvasIsOpen(false);
    setComingSoonModalIsOpen(true);
  };

  // Only records the intent. The modal is opened from the Offcanvas onExited
  // handler below, NOT here -- see the note on that prop for why.
  const handleWhatsNewClick = () => {
    setWhatsNewPending(true);
    setOffcanvasIsOpen(false);
  };

  // forceOpen skips WhatsNewModal's own onboarding/seen gate, which is the
  // point: pressing the version should always show the notes. But forceOpen
  // also deliberately does NOT stamp the flag, so without this explicit call a
  // user who reads the notes here would still get the automatic modal thrown
  // at them on the next load. Drop markWhatsNewSeen() if you'd rather a
  // deliberate peek not count against the gate.
  const handleWhatsNewClose = () => {
    markWhatsNewSeen();
    setWhatsNewIsOpen(false);
  };

  const handleSubdivisionClick = () => {
    setOffcanvasIsOpen(false);
    if (!isFullyImplemented) {
      setComingSoonModalIsOpen(true);
      return;
    }
    switch(currentCountry) {
      case 'us':
      case 'united_states_of_america':
        setStatesModalIsOpen(true);
        break;
      case 'ca':
      case 'canada':
        setProvincesModalIsOpen(true);
        break;
      case 'mx':
      case 'mexico':
        setMexicanStatesModalIsOpen(true);
        break;
      case 'uk':
      case 'united_kingdom':
        setUkKingdomsModalIsOpen(true);
        break;
      default:
        setStatesModalIsOpen(true);
    }
  };

  const handleCountriesClick = () => {
    setOffcanvasIsOpen(false);
    setCountriesModalIsOpen(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderIcon = (iconName) => {
    const iconMap = {
      pen: <Pen />,
      people: <People />,
      gavel: <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/></svg>,
      shield: <ShieldShaded />,
      coin: <Coin />,
      receipt: <ReceiptCutoff />,
      tree: <Tree />,
      globe: <Globe2 />,
      'heart-pulse': <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/><path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/></svg>,
      building: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zM4 5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zM7.5 5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zm2.5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zM4.5 8a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zm2.5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/><path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/></svg>,
      'person-arms-up': <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/><path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/></svg>,
      rocket: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/><path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Zm-4.828 7.475.04-.04-.107 1.081a1.536 1.536 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a8.548 8.548 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006ZM5.205 5c-.625.626-.94 1.351-1.004 2.09a8.497 8.497 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107-.04.039Zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a2.835 2.835 0 0 0-.045-.283 3.078 3.078 0 0 0-.3-.041Z"/><path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/></svg>,
      // Shared with the bottom navbar's Spending tab, so the two always match.
      'dollar-sign': <SpendingIcon size={20} />,
      'file-text': <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/><path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/></svg>,
      passport: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6M6 8a2 2 0 1 1 4 0 2 2 0 0 1-4 0m-.5 4a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1z"/><path d="M3.232 1.776A1.5 1.5 0 0 0 2 3.252v10.95c0 .445.191.838.49 1.11.367.422.908.688 1.51.688h8a2 2 0 0 0 2-2V4a2 2 0 0 0-1-1.732v-.47A1.5 1.5 0 0 0 11.232.321l-8 1.454ZM4 3h8a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1"/></svg>,
      house: <House />,
      bell: <Bell />,
      folder: <Folder />,
      search: <Search />,
      info: <Info />,
      lock: <Lock />,
      'heart-fill': <HeartFill />,
      'geo-alt': <GeoAlt />,
      palette: <Palette />,
      fact: <ShieldCheck/>
    };
    return iconMap[iconName] || null;
  };

  const getSubdivisionLabel = () => {
    switch(currentCountry) {
      case 'ca':
      case 'canada':
        return t('common:provincesAndTerritories', 'Provinces & Territories');
      case 'mx':
      case 'mexico':
        return t('common:states', 'States');
      case 'uk':
      case 'united_kingdom':
        return t('common:kingdoms', 'Kingdoms');
      case 'us':
      case 'united_states_of_america':
      default:
        return t('common:states', 'States');
    }
  };

  const isCurrentCountry = (countrySlug) => {
    const normalizedSlug = normalizeCountrySlug(countrySlug);
    const normalizedEffective = normalizeCountrySlug(effectiveCountry);
    return normalizedSlug === normalizedEffective;
  };

  const getCountriesList = () => {
    const activeCountries = getActiveCountries ? getActiveCountries() : [];
    const priorityCountries = [
      { slug: 'canada', displayName: 'Canada', flag: '🇨🇦' },
      { slug: 'mexico', displayName: 'Mexico', flag: '🇲🇽' },
      { slug: 'uk', displayName: 'United Kingdom', flag: '🇬🇧' },
      { slug: 'us', displayName: 'United States', flag: '🇺🇸' }
    ];
    const prioritySlugs = priorityCountries.map(c => c.slug);
    const otherCountries = [];
    if (activeCountries && activeCountries.length > 0) {
      activeCountries.forEach(country => {
        const isPriority = prioritySlugs.includes(country.slug) || 
          country.displayName === 'Canada' ||
          country.displayName === 'Mexico' ||
          country.displayName === 'United Kingdom' ||
          country.displayName === 'United States';
        if (!isPriority) {
          const isDuplicate = otherCountries.some(existing => 
            existing.slug === country.slug || 
            existing.displayName === country.displayName
          );
          if (!isDuplicate) {
            otherCountries.push(country);
          }
        }
      });
    }
    otherCountries.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return [...priorityCountries, ...otherCountries];
  };

  return (
    <>
      <Button 
        id="navbarMenuButton" 
        onClick={() => setOffcanvasIsOpen(true)}
        className="d-flex align-items-center justify-content-center"
        variant="link"
      >
        <List className="menu-icon" size={24} />
      </Button>

      <Offcanvas 
        id="mobileOffcanvas"
        placement="end" 
        show={offcanvasIsOpen} 
        onHide={() => setOffcanvasIsOpen(false)}
        className="mobile-offcanvas"
        onExited={() => {
          // The What's New handoff waits for this. Opening the modal straight
          // from the click leaves it mounting DURING the offcanvas exit
          // transition, while Bootstrap still has document.body at
          // overflow:hidden. WhatsNewModal snapshots the current overflow and
          // restores that value on close -- so it would capture "hidden" and
          // write "hidden" back permanently, killing page scroll until a
          // reload. By onExited, Bootstrap has already restored the body.
          if (whatsNewPending) {
            setWhatsNewPending(false);
            setWhatsNewIsOpen(true);
          }
        }}
      >
        <div className="offcanvas-top-spacer" />
        <Offcanvas.Header 
          className="border-bottom py-2 offcanvas-header-custom"
        >
          <Offcanvas.Title className="d-flex align-items-center offcanvas-title-custom">
            <span className="me-2">{finalCountryInfo.flag}</span>
            {finalCountryInfo.name}
          </Offcanvas.Title>
        </Offcanvas.Header>

        {/* d-flex flex-column is what lets the version row use margin-top:auto
            and sit against the bottom edge on short menus. Bootstrap's
            .offcanvas-body already carries flex-grow:1 and overflow-y:auto, and
            those compose fine with a column flex container -- the body still
            scrolls, and on a tall menu the version row simply trails the last
            section instead of floating. */}
        <Offcanvas.Body className="p-0 d-flex flex-column">
          <Nav className="flex-column">
            
            {isAllView ? (
              <>
                <div className="nav-section">
                  <Nav.Link 
                    id="offcanvasDropdownHeaderTwo"
                    onClick={handleCountriesClick}
                    className="nav-item d-flex align-items-center px-3 py-1"
                    style={{ cursor: 'pointer' }}
                  >
                    <Map className="me-2" size={16} />
                    <span>{t('common:selectCountry', 'Select a Country')}</span>
                    <ChevronRight className="ms-auto" size={14} />
                  </Nav.Link>
                </div>
              </>
            ) : isFullyImplemented ? (
              <>
                {countryConfig?.branches && (
                  <div className="nav-section">
                    <h3 id="offcanvasDropdownHeaderFour" className="nav-section-header px-3 py-1 mb-0">
                      {t('common:branches', 'Branches')}
                    </h3>
                    {countryConfig.branches.map((branch) => (
                      <Nav.Link 
                        key={branch.id}
                        id="offcanvasDropdownItem"
                        onClick={() => handleNavClick(branch.path)}
                        className="nav-item d-flex align-items-center px-3 py-1"
                      >
                        <span className="me-2">{renderIcon(branch.icon)}</span>
                        <span>{t(`common:nav_${branch.id}`, branch.title)}</span>
                      </Nav.Link>
                    ))}
                  </div>
                )}

                {countryConfig?.topics && (
                  <div className="nav-section">
                    <div 
                      id="offcanvasDropdownHeaderFour"
                      className="nav-section-header px-3 py-1 mb-0 d-flex align-items-center justify-content-between"
                      onClick={() => toggleSection('topics')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span>{t('common:topics', 'Topics')}</span>
                      <ChevronRight 
                        className={`transition-transform ${expandedSections.topics ? 'rotate-90' : ''}`}
                        size={14}
                      />
                    </div>
                    
                    {expandedSections.topics && countryConfig.topics.map((topic) => (
                      <Nav.Link 
                        key={topic.id}
                        id="offcanvasDropdownItem"
                        onClick={() => handleNavClick(topic.path)}
                        className="nav-item d-flex align-items-center px-4 py-1"
                      >
                        <span className="me-2">{renderIcon(topic.icon)}</span>
                        <span>{t(`common:nav_${topic.id}`, topic.title)}</span>
                      </Nav.Link>
                    ))}
                  </div>
                )}

                {departments.length > 0 && (
                  <div className="nav-section">
                    <div 
                      id="offcanvasDropdownHeaderTwo"
                      className="nav-section-header px-3 py-1 mb-0 d-flex align-items-center justify-content-between"
                      onClick={() => toggleSection('departments')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span>{t('common:departments', 'Departments')}</span>
                      <ChevronRight 
                        className={`transition-transform ${expandedSections.departments ? 'rotate-90' : ''}`}
                        size={14}
                      />
                    </div>
                    
                    {expandedSections.departments && departments.map((dept) => (
                      <Nav.Link 
                        key={dept.id}
                        id="offcanvasDropdownItem"
                        onClick={() => handleNavClick(dept.path)}
                        className="nav-item d-flex align-items-center px-4 py-1"
                      >
                        <span className="me-2">🏛️</span>
                        <span>{t(`common:dept_${dept.id}`, dept.title)}</span>
                      </Nav.Link>
                    ))}
                  </div>
                )}

                <div className="nav-section">
                  <Nav.Link 
                    id="offcanvasDropdownHeaderTwo"
                    onClick={handleSubdivisionClick}
                    className="nav-item d-flex align-items-center px-3 py-1"
                    style={{ cursor: 'pointer' }}
                  >
                    <GeoAlt className="me-2" size={16} />
                    <span>{getSubdivisionLabel()}</span>
                    <ChevronRight className="ms-auto" size={14} />
                  </Nav.Link>
                </div>

                <div className="nav-section">
                  <Nav.Link 
                    id="offcanvasDropdownHeaderTwo"
                    onClick={handleCountriesClick}
                    className="nav-item d-flex align-items-center px-3 py-1"
                    style={{ cursor: 'pointer' }}
                  >
                    <Map className="me-2" size={16} />
                    <span>{t('common:countries', 'Countries')}</span>
                    <ChevronRight className="ms-auto" size={14} />
                  </Nav.Link>
                </div>
              </>
             ) : (
              <>
                <div className="nav-section">
                  <div 
                    id="offcanvasDropdownHeaderFour"
                    className="nav-section-header px-3 py-1 mb-0 d-flex align-items-center justify-content-between"
                    onClick={handleComingSoonClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{t('common:branches', 'Branches')}</span>
                    <ChevronRight size={14} />
                  </div>
                </div>

                <div className="nav-section">
                  <div 
                    id="offcanvasDropdownHeaderFour"
                    className="nav-section-header px-3 py-1 mb-0 d-flex align-items-center justify-content-between"
                    onClick={handleComingSoonClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{t('common:topics', 'Topics')}</span>
                    <ChevronRight size={14} />
                  </div>
                </div>

                <div className="nav-section">
                  <Nav.Link 
                    id="offcanvasDropdownHeaderTwo"
                    onClick={handleComingSoonClick}
                    className="nav-item d-flex align-items-center px-3 py-1"
                    style={{ cursor: 'pointer' }}
                  >
                    <GeoAlt className="me-2" size={16} />
                    <span>{t('common:regions', 'Regions')}</span>
                    <ChevronRight className="ms-auto" size={14} />
                  </Nav.Link>
                </div>

                <div className="nav-section">
                  <Nav.Link 
                    id="offcanvasDropdownHeaderTwo"
                    onClick={handleCountriesClick}
                    className="nav-item d-flex align-items-center px-3 py-1"
                    style={{ cursor: 'pointer' }}
                  >
                    <Map className="me-2" size={16} />
                    <span>{t('common:countries', 'Countries')}</span>
                    <ChevronRight className="ms-auto" size={14} />
                  </Nav.Link>
                </div>
              </>
            )}

            <div className="nav-section border-top">
              <h3 id="offcanvasDropdownHeader" className="nav-section-header px-3 py-1 mb-0">
                {t('common:more', 'More')}
              </h3>
              
              {/* NOTE: IntegratedSearchNavBar documents the Preferences page as
                  mounted at /settings/location -- explicitly NOT /preferences,
                  and not the bare /settings route (that one is notification
                  settings). Verify where this lands before shipping; if the
                  route is wrong the menu entry silently 404s or falls through
                  to the catch-all. */}
              <Nav.Link 
                id="offcanvasDropdownItem"
                onClick={() => handleNavClick('/preferences' )}
                className="nav-item d-flex align-items-center px-3 py-1"
              >
                <Gear className="me-2" size={16} />
                <span>{t('common:preferences', 'Preferences')}</span>
              </Nav.Link>

              {/* Moved out of BottomNavbar -- Spending took its tab. Directly
                  under Preferences, and country-agnostic like it. */}
              <Nav.Link 
                id="offcanvasDropdownItem"
                onClick={() => handleNavClick('/collection')}
                className="nav-item d-flex align-items-center px-3 py-1"
              >
                <Folder className="me-2" size={16} />
                <span>{t('common:collection', 'Collection')}</span>
              </Nav.Link>

              {/* Moved out of BottomNavbar -- the fact checker now lives here only. */}
              <Nav.Link 
                id="offcanvasDropdownItem"
                onClick={() => handleNavClick('/fact-check')}
                className="nav-item d-flex align-items-center px-3 py-1"
              >
                <ShieldCheck className="me-2" size={16} />
                <span>{t('common:factChecker', 'Fact Checker')}</span>
              </Nav.Link>

              {effectiveCountry === 'us' && (
                <Nav.Link 
                  id="offcanvasDropdownItem"
                  onClick={() => handleNavClick('/social')}
                  className="nav-item d-flex align-items-center px-3 py-1"
                >
                  <People className="me-2" size={16} />
                  <span>{t('common:social', 'Social')}</span>
                </Nav.Link>
              )}
  
              
              {utilityNavItems.filter((item) => !isListedByHand(item)).map((item) => (
                <Nav.Link 
                  key={item.id}
                  id="offcanvasDropdownItem"
                  onClick={() => handleNavClick(item.path, item.external)}
                  className={`nav-item d-flex align-items-center px-3 py-1 ${item.className || ''}`}
                >
                  <span className="me-2">{renderIcon(item.icon)}</span>
                  <span>{t(`common:nav_${item.id}`, item.title)}</span>
                </Nav.Link>
              ))}
            </div>
          </Nav>

          {/* Reuses common:whatsNewVersionLabel, the same key the modal header
              renders, so the label and the dialog it opens can never disagree
              and no new string needs translating. A real <button> rather than a
              styled div: this is the only keyboard-reachable way into the
              release notes once the automatic modal has been dismissed. */}
          <div className="offcanvas-version-row">
            <button
              type="button"
              className="offcanvas-version-btn"
              onClick={handleWhatsNewClick}
            >
              {t('common:whatsNewVersionLabel', 'Version {{version}}', {
                version: WHATS_NEW_VERSION,
              })}
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Portaled to document.body, and that is the fix for the clipped modal.
          WhatsNewModal's .wn-overlay is position:fixed with top/bottom:0, which
          normally resolves against the viewport -- but a transform, filter,
          backdrop-filter, perspective, will-change or contain on ANY ancestor
          makes that ancestor the containing block for fixed descendants
          instead. Rendered inline here, the overlay sized itself to whichever
          transformed ancestor sits above this component in the navbar tree, so
          the centered modal overflowed that short box and its top was clipped
          with no way to scroll to it. The portal moves the DOM node out from
          under the transform while keeping it in this React tree.

          Mounted only while open, separately on purpose: WhatsNewModal reads
          forceOpen in an effect keyed on [evaluate, forceOpen, pathname]. Kept
          always-mounted with forceOpen pinned true, its internal show state
          goes false on close and nothing re-runs the effect, so a second press
          would do nothing. Unmounting gives a fresh mount every press. */}
      {whatsNewIsOpen && createPortal(
        <WhatsNewModal forceOpen onClose={handleWhatsNewClose} />,
        document.body
      )}

      <Modal 
        show={comingSoonModalIsOpen} 
        size="sm" 
        centered 
        onHide={() => setComingSoonModalIsOpen(false)}
        className="coming-soon-modal"
      >   
        <Modal.Header id='stateModalHeader' closeButton>
          <h5 style={{ margin: 0 }}>{t('common:comingSoon', 'Coming Soon')}</h5>
        </Modal.Header>
        <Modal.Body id="modalBodyStyle">
          <div className="coming-soon-content">
            <p className="coming-soon-text">
              {t('common:workingOnCoverage', "We're working on {{country}} coverage.", { country: finalCountryInfo.name })}
            </p>
            <p className="coming-soon-subtext">
              {t('common:checkBackSoon', 'Check back soon!')}
            </p>
          </div>
        </Modal.Body>                              
      </Modal>

      <Modal 
        show={countriesModalIsOpen} 
        size="lg" 
        centered 
        onHide={() => setCountriesModalIsOpen(false)}
        className="countries-modal"
      >   
        <Modal.Header id='stateModalHeader' closeButton>
          <h3><Map className="me-2"/>{t('common:selectCountry', 'Select a Country')}</h3>
        </Modal.Header>
        <Modal.Body id="modalBodyStyle">
          <div className="countries-grid-container">
            {getCountriesList().map(country => {
              const isCurrent = isCurrentCountry(country.slug);
              return (
                <Link 
                  key={country.slug}
                  className="country-button-link"
                  onClick={() => {
                    setCountriesModalIsOpen(false);
                    localStorage.setItem('selectedCountry', country.slug);
                    handleNavClick(`/${country.slug}`);
                  }}
                  to={`/${country.slug}`}
                >
                  <div className={`country-button ${isCurrent ? 'country-button-active' : ''}`}>
                    <span className="country-flag">{country.flag}</span>
                    <span className="country-name">{country.displayName}</span>
                    {isCurrent && <span className="country-current-indicator">✓</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </Modal.Body>                              
      </Modal>

      {isFullyImplemented && (
        <>
          <Modal 
            id='stateModal' 
            show={statesModalIsOpen} 
            size="xl" 
            aria-labelledby="contained-modal-title-vcenter" 
            centered 
            onHide={() => setStatesModalIsOpen(false)}
          >   
            <Modal.Header id='stateModalHeader' closeButton>
              <h3><GeoAlt/>{t('common:selectState', 'Select a State')}</h3>
            </Modal.Header>                         
            <StateButtonPage onStateSelect={() => setStatesModalIsOpen(false)}/>                               
          </Modal>

          <Modal 
            id='provinceModal' 
            show={provincesModalIsOpen} 
            size="xl" 
            aria-labelledby="contained-modal-title-vcenter" 
            centered 
            onHide={() => setProvincesModalIsOpen(false)}
          >   
            <Modal.Header id='stateModalHeader' closeButton>
              <h3><GeoAlt/>{t('common:selectProvince', 'Select a Province or Territory')}</h3>
            </Modal.Header>                         
            <ProvinceButtonPage onProvinceSelect={() => setProvincesModalIsOpen(false)}/>                               
          </Modal>

          <Modal 
            id='mexicanStateModal' 
            show={mexicanStatesModalIsOpen} 
            size="xl" 
            aria-labelledby="contained-modal-title-vcenter" 
            centered 
            onHide={() => setMexicanStatesModalIsOpen(false)}
          >   
            <Modal.Header id='stateModalHeader' closeButton>
              <h3><GeoAlt/>{t('common:selectState', 'Select a State')}</h3>
            </Modal.Header>                         
            <MexicanStateButtonPage onStateSelect={() => setMexicanStatesModalIsOpen(false)}/>                               
          </Modal>

          <Modal 
            id='ukKingdomModal' 
            show={ukKingdomsModalIsOpen} 
            size="xl" 
            aria-labelledby="contained-modal-title-vcenter" 
            centered 
            onHide={() => setUkKingdomsModalIsOpen(false)}
          >   
            <Modal.Header id='stateModalHeader' closeButton>
              <h3><GeoAlt/>{t('common:selectKingdom', 'Select a Kingdom')}</h3>
            </Modal.Header>                         
            <KingdomButtonPage onKingdomSelect={() => setUkKingdomsModalIsOpen(false)}/>                               
          </Modal>
        </>
      )}

<style>{`
        #navbarMenuButton {
          background: var(--search-bg, #033b5a);
          border-color: transparent;
          border-radius: 50px;
          height: 36px;
          width: 36px;
          padding: 0.5rem;
          margin-left: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .menu-icon {
          color: var(--text-primary, white);
        }

        @media (max-width: 380px) {
          #navbarMenuButton {
            padding: 0.375rem;
          }
        }

        #mobileOffcanvas {
          color: var(--text-primary, white);
          width: 50%;
          background-color: var(--offcanvas-bg, #033b5a); 
          border-color: transparent;
          outline-color: transparent;
          backdrop-filter: none;
          z-index: 1060 !important;
        }

        /* Desktop: cap the panel width so it reads as a menu, not a half-screen sheet */
        @media (min-width: 768px) {
          #mobileOffcanvas {
            width: 340px;
            max-width: 90vw;
          }
        }

        /* ------------------------------------------------------------------
           Offcanvas header band (the spacer strip plus the country header).

           These two elements are ALWAYS the same color, so they share one rule
           and read one variable. --offcanvas-header-bg is the only knob; every
           value for it lives in theme.css next to --offcanvas-bg and
           --offcanvas-item-bg, and --modal-header-bg is the fallback for a
           theme that hasn't defined one.

           Do NOT reintroduce per-theme overrides here, in either form. The app
           sets data-theme AND data-mode together (classic-light runs as
           [data-theme="classic-light"][data-mode="light"]), and theme.css
           resolves that pair by source order -- the later block wins. A rule in
           this file can't participate in that ordering, and worse, setting the
           variable on the elements themselves shadows the inherited :root
           value no matter which palette declared it. That is exactly how the
           band got stuck on the comfort-light beige while every other
           classic-light color resolved correctly. One declaration per palette,
           in theme.css, is what keeps the two attributes composing properly.
           ------------------------------------------------------------------ */
        .offcanvas-top-spacer,
        .offcanvas-header-custom {
          background-color: var(--offcanvas-header-bg, var(--modal-header-bg, #002539));
        }

        .offcanvas-top-spacer {
          height: 60px;
          flex-shrink: 0;
        }

        .offcanvas-header-custom {
          padding-top: max(env(safe-area-inset-top, 0px), 12px);
        }

        .offcanvas-title-custom {
          font-size: 16px;
        }

        /* margin-top:auto pushes this to the bottom edge; the safe-area padding
           mirrors .offcanvas-header-custom so the row clears the home indicator
           in the Capacitor build. Takes its color from the panel via
           currentColor rather than declaring per-theme values -- see the header
           band note above for why theme overrides don't belong in this file. */
        .offcanvas-version-row {
          margin-top: auto;
          flex-shrink: 0;
          padding: 10px 12px max(env(safe-area-inset-bottom, 0px), 12px);
          border-top: 1px solid var(--border-light, rgba(0,0,0,0.1));
          text-align: center;
        }

        .offcanvas-version-btn {
          background: none;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          letter-spacing: 0.02em;
          white-space: nowrap;
          color: currentColor;
          opacity: 0.6;
          cursor: pointer;
          transition: opacity 0.2s ease, background-color 0.2s ease;
        }

        .offcanvas-version-btn:hover {
          opacity: 1;
          background-color: rgba(127, 127, 127, 0.15);
        }

        .offcanvas-version-btn:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
          opacity: 1;
        }

        @media (prefers-reduced-motion: reduce) {
          .offcanvas-version-btn {
            transition: none;
          }
        }

        #offcanvasDropdownHeaderFour {
          color: var(--text-primary, white);
          font-size: 14px;
          background-color: transparent;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          opacity: 0.8;
        }

        #offcanvasDropdownItem {
          color: var(--text-primary, white);
          background-color: var(--offcanvas-item-bg, #035279);
          border-color: transparent;
          border-radius: 6px;
          padding: 6px 8px;
          margin: 2px 4px;
          text-decoration: none;
          display: inline;
          font-size: 14px;
        }

        #offcanvasDropdownHeader {
          color: var(--text-primary, white);
          font-size: 14px;
          background-color: transparent;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          opacity: 0.8;
        }

        #offcanvasDropdownHeaderTwo {
          color: var(--text-primary, white);
          font-size: 14px;
          background-color: transparent;
          padding: 4px;
        }

        #offcanvasDropdownHeaderThree {
          color: var(--text-primary, white);
          font-size: 14px;
          background-color: transparent;
        }
        
        .nav-section {
          border-bottom: 1px solid var(--border-light, rgba(0,0,0,0.1));
          padding: 4px 0;
        }
        
        .nav-section:last-child {
          border-bottom: none;
        }
        
        .nav-section-header {
          background-color: rgba(0,0,0,0.03);
          font-weight: 600;
          font-size: 12px;
          letter-spacing: 0.5px;
        }
        
        .nav-item {
          transition: all 0.2s ease;
          border: none;
          font-size: 14px;
        }
        
        .nav-item svg {
          transition: all 0.2s ease;
          width: 16px;
          height: 16px;
        }
        
        .nav-item:hover svg {
          transform: scale(1.1);
        }
        
        .transition-transform {
          transition: transform 0.2s ease;
        }
        
        .rotate-90 {
          transform: rotate(90deg);
        }
        
        .text-danger {
          color: var(--bs-danger) !important;
        }

        #stateModal, #provinceModal, #mexicanStateModal, #ukKingdomModal {
          padding-top: 50px;
        }

        #stateModalHeader {
          background-color: var(--modal-header-bg, #002539);
          color: var(--text-primary, white);
          border-color: var(--modal-header-bg, #002539);
        }

        #homebg {
          background-color: var(--bg-primary); 
          overscroll-behavior: none;
        }

        #modalBodyStyle {
          padding: 0;
          background-color: var(--bg-primary);
        }

        .coming-soon-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          background-color: var(--bg-primary);
          text-align: center;
        }

        .coming-soon-text {
          color: var(--text-secondary, rgba(255,255,255,0.9));
          margin-bottom: 0.5rem;
          font-size: 14px;
        }

        .coming-soon-subtext {
          color: var(--text-muted, rgba(255,255,255,0.6));
          font-size: 12px;
          margin-bottom: 0;
        }

        .countries-grid-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          padding: 20px;
          background-color: var(--bg-primary);
        }

        @media (max-width: 576px) {
          .countries-grid-container {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            padding: 16px;
          }
        }

        .country-button-link {
          text-decoration: none;
          display: block;
        }

        .country-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 16px 12px;
          background-color: var(--bg-button-secondary, #0051835b);
          border-radius: 16px;
          box-shadow: var(--shadow-color, 2px 2px 12px rgba(0, 0, 0, 0.192));
          transition: transform 0.2s ease, background-color 0.2s ease;
          min-height: 90px;
          position: relative;
        }

        .country-button:active {
          transform: scale(0.98);
        }

        .country-button-active {
          background-color: var(--accent-secondary, #006699) !important;
          border: 2px solid var(--text-primary, #ffffff);
          box-shadow: 0 0 15px var(--accent-glow, rgba(0, 102, 153, 0.5));
        }
        
        .country-button-active .country-name {
          color: var(--text-primary, #ffffff);
          font-weight: 600;
        }

        .country-current-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          font-size: 14px;
          color: var(--bg-primary);
          font-weight: bold;
        }

        .country-flag {
          font-size: 32px;
          margin-bottom: 8px;
          line-height: 1;
        }

        .country-name {
          color: var(--text-primary, white);
          font-size: 13px;
          font-weight: 500;
          text-align: center;
          line-height: 1.2;
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        }

        @media (max-width: 576px) {
          .country-button {
            padding: 14px 10px;
            min-height: 85px;
          }

          .country-flag {
            font-size: 28px;
            margin-bottom: 6px;
          }

          .country-name {
            font-size: 12px;
          }
        }

        [data-theme="light"] #navbarMenuButton {
          background: var(--search-bg, #f0f2f5);
        }

        [data-theme="light"] .menu-icon {
          color: var(--text-primary, #000000);
        }

        [data-theme="light"] #mobileOffcanvas {
          background-color: var(--bg-primary, #ffffff);
          color: var(--text-primary, #000000);
        }

        [data-theme="light"] #offcanvasDropdownItem {
          background-color: var(--bg-secondary, #f0f2f5);
          color: var(--text-primary, #000000);
        }

        [data-theme="light"] #offcanvasDropdownHeaderFour,
        [data-theme="light"] #offcanvasDropdownHeader,
        [data-theme="light"] #offcanvasDropdownHeaderTwo {
          color: var(--text-primary, #000000);
        }

        [data-theme="light"] .nav-section {
          border-bottom-color: var(--border-primary, #dee2e6);
        }

        [data-theme="light"] #stateModalHeader {
          background-color: var(--bg-secondary, #f0f2f5);
          color: var(--text-primary, #000000);
          border-color: var(--border-primary, #dee2e6);
        }

        [data-theme="light"] .coming-soon-text {
          color: var(--text-primary, #000000);
        }

        [data-theme="light"] .coming-soon-subtext {
          color: var(--text-tertiary, #666666);
        }

        [data-theme="light"] .country-button {
          background-color: var(--bg-card, #f8f9fa);
          border: 1px solid var(--border-primary, #dee2e6);
        }

        [data-theme="light"] .country-name {
          color: var(--text-primary, #000000);
        }

        [data-theme="light"] .country-button-active {
          background-color: var(--accent-primary, #016cae) !important;
          border-color: var(--accent-primary, #016cae);
        }

        [data-theme="light"] .country-button-active .country-name {
          color: white;
        }

        [data-theme="light"] .country-current-indicator {
          color: white;
        }

        [data-theme="daylight"] #navbarMenuButton {
          background: #ffffff;
          border: 1px solid #e0e0e0;
        }

        [data-theme="daylight"] .menu-icon {
          color: #1a1a1a;
        }

        [data-theme="daylight"] #mobileOffcanvas {
          background-color: var(--bg-primary);
          color: #1a1a1a;
        }

        [data-theme="daylight"] #offcanvasDropdownItem {
          background-color: #ffffff;
          color: #1a1a1a;
        }

        [data-theme="daylight"] #offcanvasDropdownItem:hover {
          background-color: #f5f5f5;
        }

        [data-theme="daylight"] #offcanvasDropdownHeaderFour,
        [data-theme="daylight"] #offcanvasDropdownHeader,
        [data-theme="daylight"] #offcanvasDropdownHeaderTwo {
          color: #1a1a1a;
        }

        [data-theme="daylight"] .nav-section {
          border-bottom-color: #e0e0e0;
        }

        [data-theme="daylight"] #stateModalHeader {
          background-color: #f5f5f5;
          color: #1a1a1a;
          border-color: #e0e0e0;
        }

        [data-theme="daylight"] .coming-soon-content {
          background-color: #f0f0f0;
        }

        [data-theme="daylight"] .coming-soon-text {
          color: #1a1a1a;
        }

        [data-theme="daylight"] .coming-soon-subtext {
          color: #666666;
        }

        [data-theme="daylight"] .country-button {
          background-color: #ffffff;
          border: 1px solid #e0e0e0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        [data-theme="daylight"] .country-button:hover {
          border-color: #f97316;
        }

        [data-theme="daylight"] .country-name {
          color: #1a1a1a;
        }

        [data-theme="daylight"] .country-button-active {
          background-color: #f97316 !important;
          border-color: #f97316;
          box-shadow: 0 0 15px rgba(249, 115, 22, 0.3);
        }

        [data-theme="daylight"] .country-button-active .country-name {
          color: white;
        }

        [data-theme="daylight"] .country-current-indicator {
          color: white;
        }

        [data-theme="daylight"] #modalBodyStyle {
          background-color: #f0f0f0;
        }

        [data-mode="light"] #navbarMenuButton {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
        }

        [data-mode="light"] .menu-icon {
          color: var(--text-primary);
        }

        [data-mode="light"] #mobileOffcanvas {
          background-color: var(--bg-primary);
          color: var(--text-primary);
        }

        [data-mode="light"] #offcanvasDropdownItem {
          background-color: var(--bg-card);
          color: var(--text-primary);
        }

        [data-mode="light"] #offcanvasDropdownItem:hover {
          background-color: #FAF8F5;
        }

        [data-mode="light"] #offcanvasDropdownHeaderFour,
        [data-mode="light"] #offcanvasDropdownHeader,
        [data-mode="light"] #offcanvasDropdownHeaderTwo {
          color: var(--text-primary);
        }

        [data-mode="light"] .nav-section {
          border-bottom-color: var(--border-primary);
        }

        [data-mode="light"] #stateModalHeader {
          background-color: var(--bg-primary);
          color: var(--text-primary);
          border-color: var(--border-primary);
        }

        [data-mode="light"] .coming-soon-content {
          background-color: var(--bg-primary);
        }

        [data-mode="light"] .coming-soon-text {
          color: var(--text-primary);
        }

        [data-mode="light"] .coming-soon-subtext {
          color: #6B6B6B;
        }

        [data-mode="light"] .countries-grid-container {
          background-color: var(--bg-primary);
        }

        [data-mode="light"] .country-button {
          background-color: var(--bg-card);
          border: 1px solid var(--border-primary);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        [data-mode="light"] .country-button:hover {
          border-color: #016cae;
        }

        [data-mode="light"] .country-name {
          color: var(--text-primary);
        }

        [data-mode="light"] .country-button-active {
          background-color: #016cae !important;
          border-color: #016cae;
          box-shadow: 0 0 15px rgba(1, 108, 174, 0.2);
        }

        [data-mode="light"] .country-button-active .country-name {
          color: white;
        }

        [data-mode="light"] .country-current-indicator {
          color: white;
        }

        [data-mode="light"] #modalBodyStyle {
          background-color: var(--bg-primary);
        }

        [data-mode="dark"] #navbarMenuButton {
          background: #1E1E1E;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        [data-mode="dark"] .menu-icon {
          color: #E8E8E8;
        }

        [data-mode="dark"] #mobileOffcanvas {
          background-color: #1A1A1A;
          color: #E8E8E8;
        }

        [data-mode="dark"] #offcanvasDropdownItem {
          background-color: #1E1E1E;
          color: #E8E8E8;
        }

        [data-mode="dark"] #offcanvasDropdownItem:hover {
          background-color: #252525;
        }

        [data-mode="dark"] #offcanvasDropdownHeaderFour,
        [data-mode="dark"] #offcanvasDropdownHeader,
        [data-mode="dark"] #offcanvasDropdownHeaderTwo {
          color: #E8E8E8;
        }

        [data-mode="dark"] .nav-section {
          border-bottom-color: rgba(255, 255, 255, 0.1);
        }

        [data-mode="dark"] #stateModalHeader {
          background-color: #1A1A1A;
          color: #E8E8E8;
          border-color: rgba(255, 255, 255, 0.1);
        }

        [data-mode="dark"] .coming-soon-content {
          background-color: #1E1E1E;
        }

        [data-mode="dark"] .coming-soon-text {
          color: #E8E8E8;
        }

        [data-mode="dark"] .coming-soon-subtext {
          color: #787878;
        }

        [data-mode="dark"] .countries-grid-container {
          background-color: #121212;
        }

        [data-mode="dark"] .country-button {
          background-color: #1E1E1E;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        [data-mode="dark"] .country-button:hover {
          border-color: #81C7F5;
        }

        [data-mode="dark"] .country-name {
          color: #E8E8E8;
        }

        [data-mode="dark"] .country-button-active {
          background-color: #81C7F5 !important;
          border-color: #81C7F5;
          box-shadow: 0 0 15px rgba(129, 199, 245, 0.25);
        }

        [data-mode="dark"] .country-button-active .country-name {
          color: #121212;
        }

        [data-mode="dark"] .country-current-indicator {
          color: #121212;
        }

        [data-mode="dark"] #modalBodyStyle {
          background-color: #121212;
        }

      `}</style>
    </>
  );
};

export default MobileNavigation;